import { useApolloClient } from '@apollo/react-hooks';
import { useCallback, useContext } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { RightRailContext } from '../components/PageItemsRail';
import { pageQuery } from '../components/PageItemsRail/graphql';
import { PageQuery, Wws_PageItem } from '../generated/graphql';
import { previewPageRoute, previewRoute } from '../helpers/routes';
import { TrackingContext } from '../TrackingContext';
import { getTargetOrigin } from './getTargetOrigin';

export interface SharedFormProps {
  close: () => void;
  loading?: boolean;
  updating: boolean;
  submitForm: () => void;
  setFieldValue: (field: string, value: any) => void;
  isNew: boolean;
  discardOrDelete: () => void;
  pageId: number;
  hiddenFields?: string[];
}

export const useManageItem = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const params = useParams<{ pageId: string; itemId?: string }>();
  const pageId = parseInt(params.pageId || '');
  const itemId = params.itemId ? parseInt(params.itemId) : null;
  const client = useApolloClient();
  const { previewOnClose, iframeRef } = useContext(RightRailContext);
  const { track } = useContext(TrackingContext);

  const goBack = history.goBack;
  const isNew = !itemId;

  const pageData = useCallback(
    () =>
      client.readQuery<PageQuery>({
        query: pageQuery,
        variables: {
          id: pageId,
        },
      }),
    [pageId, client]
  );

  const trackItem = (pageItem?: Wws_PageItem) => {
    const onPreview = pathname.includes('preview');
    track(`Item ${isNew ? 'Added' : 'Edited'}`, {
      action: isNew ? 'add' : 'edit',
      actionName: `${isNew ? 'Add' : 'Edit'} Page Item`,
      page: pageData()?.page?.title,
      item: pageItem?.type,
      source: onPreview ? 'preview' : 'dashboard',
    });
  };

  const close = (pageItem?: Wws_PageItem) => {
    trackItem(pageItem);

    if (pathname.includes('preview')) {
      const updatedItems = (pageData()?.page?.items || []).map(item => {
        if (pageItem && item.id === pageItem?.id) {
          return pageItem;
        }
        return item;
      });

      iframeRef?.current?.contentWindow?.postMessage(
        JSON.stringify({
          type: 'updatePage',
          data: {
            ...pageData()?.page,
            items: updatedItems,
          },
        }),
        getTargetOrigin()
      );
      history.push(previewRoute);
    } else if (previewOnClose) {
      history.push(previewPageRoute(pageData()?.page?.routeName || ''));
    } else {
      history.push('/gs/dashboard');
    }
  };

  return {
    pageId,
    itemId,
    close,
    trackItem,
    goBack,
    isNew,
  };
};
